import React, { FC } from 'react'

import { Button, Level } from '@smarty-nx/ds'
import { TRACKING_CATEGORIES } from '@smarty-nx/tracking'
import cn from 'classnames'
import { graphql, HeadProps, Link } from 'gatsby'

import classes from './social-tariff.module.scss'
import EllipseCutOut from '../../components/EllipseCutOut'
import PageHead from '../../components/PageHead'
import { Header2, SiteContainer } from '../../components/shared'
import spacerClasses from '../../components/styles/spacer.module.scss'
import withApp from '../../components/withApp/withApp'
import {
  createFAQPageSchema,
  mapFaqItemsToSchemaItems,
} from '../../constants/structuredData'
import { useIsAuthenticated } from '../../hooks'
import AllPlans from '../../modular-system/AllPlans'
import FaqSection from '../../modular-system/FaqSection'
import { FaqSectionProps } from '../../modular-system/FaqSection/FaqSection'
import piggyBank from '../../static/images/refurbished-phones-page/piggybank.svg'
import calendar from '../../static/images/social-tariff/calendar.svg'
import checkMark from '../../static/images/social-tariff/checkmark.svg'
import noContract from '../../static/images/social-tariff/no-contract.svg'
import simInHand from '../../static/images/social-tariff/sim-in-hand.svg'
import socialTariff from '../../static/images/social-tariff/social-tariff.webp'
import { initWebChat } from '../../utils/genesys'

interface SocialTarrifProps {
  data: {
    contentfulPageSection: FaqSectionProps
  }
  location: { pathname: string }
}

const SocialTariff: FC<SocialTarrifProps> = ({
  data: {
    contentfulPageSection: { header, items, link },
  },
}) => {
  const isAuthenticated = useIsAuthenticated()

  return (
    <div className={classes.socialTariff}>
      <div className={classes.socialTariffBanner}>
        <div className={classes.socialTariffBannerContent}>
          <img
            src={simInHand}
            className={classes.simInHandIcon}
            alt=""
            loading="lazy"
            decoding="async"
          />
          <h1>SMARTY Social Tariff</h1>
          <p>
            Staying in touch is an essential part of life, which is why we’re
            launching the SMARTY Social Tariff. A low-cost unlimited plan for
            those who need it most, available for new and existing customers on
            eligible benefits.
          </p>
          <div className={classes.socialBenefits}>
            <div className={classes.item}>
              <img src={checkMark} alt="" loading="lazy" decoding="async" />
              <span>Fast eligibility checks with no credit checks</span>
            </div>
            <div className={classes.item}>
              <img src={piggyBank} alt="" loading="lazy" decoding="async" />
              <span>Affordable pricing to help you stay connected</span>
            </div>
            <div className={classes.item}>
              <img src={calendar} alt="" loading="lazy" decoding="async" />
              <span>
                Once accepted, keep the plan for as long as you`&apos;re
                eligible
              </span>
            </div>
            <div className={classes.item}>
              <img src={noContract} alt="" loading="lazy" decoding="async" />
              <span>Ultimate flexibility - 1 month rolling plan</span>
            </div>
          </div>

          {isAuthenticated ? (
            <div className={classes.actions}>
              <Button
                data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                data-item-name=""
                data-testid="get-smarty-social-tariff-button"
                onClick={() => initWebChat('website-socialTariff')}
              >
                Get SMARTY Social Tariff
              </Button>
            </div>
          ) : (
            <>
              <div className={cn(classes.actions, classes.twoColumns)}>
                <Button
                  as={(props) => (
                    <Link
                      {...props}
                      to="/sim-in-hand"
                      data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                      data-item-name="go-to-social-tariff-check"
                      data-testid="social-tariff-check-button"
                    >
                      I’m new to SMARTY
                    </Link>
                  )}
                />

                <Button
                  data-testid="social-tariff-with-smarty-button"
                  level={Level.Secondary}
                  onClick={() => initWebChat('website-socialTariff')}
                >
                  I’m already with SMARTY
                </Button>
              </div>

              <div className={classes.links}>
                <Button
                  level={Level.Tertiary}
                  as={(props) => (
                    <a
                      {...props}
                      href="https://www.three.co.uk/vulnerability/financial-support"
                      data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                      data-item-name=""
                      data-testid=""
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      I’m already with Three
                    </a>
                  )}
                />
              </div>
            </>
          )}
        </div>

        <EllipseCutOut className={classes.cutOut} />
      </div>

      <SiteContainer>
        <div className={classes.pricingSection}>
          <div className={classes.left}>
            <img src={socialTariff} alt="" loading="lazy" decoding="async" />
          </div>

          <div className={classes.right}>
            <Header2>SMARTY Social Tariff Pricing</Header2>
            <p>
              As a SMARTY Social Tariff customer you get unlimited data for just
              £12 a month. As standard, you’ll also have unlimited UK calls and
              texts, 5G at no extra cost and EU Roaming (up to 12GB) included,
              all on a flexible 1 month plan with no annual price rises.
            </p>
          </div>
        </div>
      </SiteContainer>
      <SiteContainer className={spacerClasses.spacerBottom}>
        <div className={classes.planIncludes}>
          <AllPlans
            headerTestId="all-plans-include-title"
            listTestId="all-plans-include-list-item"
            iconTestId="all-plans-check-icon"
            variation="socialTariff"
          />
        </div>
      </SiteContainer>
      <div className={classes.darkestGreyBlock}>
        <SiteContainer
          className={`${spacerClasses.spacerTop} ${spacerClasses.spacerBottom}`}
        >
          <FaqSection header={header} items={items} link={link} />
        </SiteContainer>
      </div>
    </div>
  )
}

export default withApp(SocialTariff)

export const Head: FC<HeadProps<SocialTarrifProps['data']>> = ({
  data,
  location,
}) => {
  const faqSchema = createFAQPageSchema(
    mapFaqItemsToSchemaItems(data.contentfulPageSection?.items || [])
  )

  return (
    <PageHead
      title="Social Tariffs SIM plans with no contract, no credit check."
      description="Get discounted SIM deals, 'Social Tariffs' for those on certain benefits. SMARTY Mobile, Simple honest Mobile. "
      location={location}
    >
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </PageHead>
  )
}

export const query = graphql`
  query {
    contentfulPageSection(sectionName: { eq: "st-faq-list" }) {
      ...FaqSection
    }
  }
`
